import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Checkbox, FormControlLabel,
  Dialog, DialogTitle, DialogContent, IconButton, Grid
} from '@mui/material';
import {
  Close as CloseIcon, PlayArrow as PlayIcon, Description as DescriptionIcon
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import { hasReadPdf, hasWatchedVideo } from '../../hooks/TimeTracking';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';


const InductionStep = ({ data, onChange }) => {
  const { user, getAuthHeaders } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [videoOpen, setVideoOpen] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [pdfStartTime, setPdfStartTime] = useState(null);
  const [watchedSeconds, setWatchedSeconds] = useState(0);
  const [lastPlayedSeconds, setLastPlayedSeconds] = useState(0);
  const [showEarlyCloseWarning, setShowEarlyCloseWarning] = useState(false);

 useEffect(() => {
  const fetchDocumentsAndProgress = async () => {
    try {
      // Fetch documents
      const docRes = await fetch(`${API_URL}/documents?module=Induction`, {
        headers: getAuthHeaders()
      });
      const docData = await docRes.json();
      setDocuments(docData.documents);

      // Fetch progress
      const progressRes = await fetch(`${API_URL}/progress/user-progress?userId=${user.id}&module=Induction`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!progressRes.ok) {
        throw new Error('Failed to fetch user progress');
      }

      const progressData = await progressRes.json();
       console.log('Progress Data:', progressData);
      // Assuming onChange expects a map of documentId: status
      onChange(progressData);
    } catch (error) {
      console.error('Error fetching documents or progress:', error);
    }
  };

  if (user) {
    fetchDocumentsAndProgress();
  }
}, [user, getAuthHeaders]);


  const groupedBySubModule = documents.reduce((acc, doc) => {
    if (!acc[doc.subModule]) acc[doc.subModule] = [];
    acc[doc.subModule].push(doc);
    return acc;
  }, {});

 const updateProgress = async (documentId) => {
  try {
    await fetch(`${API_URL}/progress/user-progress`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        userId: user.id,
        documentId,
        status: true
      })
    });
  } catch (error) {
    console.error('Failed to update progress:', error);
  }
};

const handleDocumentRead = async (documentId) => {
  onChange({ ...data, [documentId]: true });
  if (currentDocument?.isTrackable) {
    await updateProgress(documentId);
  }
};

const handleVideoComplete = async (documentId) => {
  onChange({ ...data, [documentId]: true });
  if (currentDocument?.isTrackable) {
    await updateProgress(documentId);
  }
};


  const handleOpenDocument = (doc) => {
    setCurrentDocument(doc);
    setDocumentOpen(true);
    setPdfStartTime(Date.now());
  };
const handleCloseDocument = async () => {
  const endTime = Date.now();
  const alreadyCompleted = data[currentDocument.documentId];
  const isTrackable = currentDocument.isTrackable;

  if (!isTrackable || alreadyCompleted || hasReadPdf(pdfStartTime, endTime, currentDocument.pageCount)) {
    if (!alreadyCompleted) {
      await handleDocumentRead(currentDocument.documentId);
    }
    setDocumentOpen(false);
  } else {
    setShowEarlyCloseWarning(true);
  }
};



  const handleOpenVideo = (doc) => {
    setCurrentDocument(doc);
    setVideoOpen(true);
    setWatchedSeconds(0);
    setLastPlayedSeconds(0);
  };

  const handleVideoProgress = ({ playedSeconds }) => {
    const delta = playedSeconds - lastPlayedSeconds;
    if (delta > 0 && delta < 5) {
      setWatchedSeconds(prev => prev + delta);
    }
    setLastPlayedSeconds(playedSeconds);
  };

  const handleVideoEnd = async () => {
    const isWatched = hasWatchedVideo(watchedSeconds, currentDocument.duration);
    if (isWatched) {
      await handleVideoComplete(currentDocument.documentId);
    }
  };
const handleCloseVideo = async () => {
  const alreadyCompleted = data[currentDocument.documentId];
  const isTrackable = currentDocument.isTrackable;

  if (!isTrackable || alreadyCompleted || hasWatchedVideo(watchedSeconds, currentDocument.duration)) {
    if (!alreadyCompleted) {
      await handleVideoComplete(currentDocument.documentId);
    }
    setVideoOpen(false);
  } else {
    setShowEarlyCloseWarning(true);
  }
};



  if (!user) return <div>Loading...</div>;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="h4" sx={{ fontWeight: '600', fontSize: '20px' }} gutterBottom>
        Welcome to Your Induction
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '14px', mb: 5 }}>
      Your journey with Divergent Insights begins here, and we're excited to walk alongside you. Complete this section to dive deep into understanding our culture, and connect with your new workplace. Watch the videos, read through the policies, and make the most of the resources available to you.
      </Typography>
      <Grid container spacing={2}>
        {Object.entries(groupedBySubModule).map(([subModule, docs], index) => (
          <Grid item xs={12} key={subModule}>
            {index > 0 && <br />}
            <Typography variant="h6" sx={{ 
              mb: 3, 
              fontSize: '22px', 
              fontWeight: 700,
              color: '#2c3e50',
              borderBottom: '2px solid #e8eaed',
              pb: 1.5,
              textAlign: 'center'
            }}>
              {subModule}
            </Typography>
            <Grid container spacing={3}>
              {docs.map((doc) => (
                <Grid item xs={12} sm={6} key={doc.documentName}>
                  <Card sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    border: '1px solid #f0f0f0',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                      borderColor: '#e0e0e0'
                    }
                  }}>
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                        {doc.docType === 'video/mp4' ? (
                          <PlayIcon color="primary" sx={{ mr: 2, fontSize: '2.5rem', mt: 0.5, color: '#7e57c2' }} />
                        ) : (
                          <DescriptionIcon color="primary" sx={{ mr: 2, fontSize: '2.5rem', mt: 0.5, color: '#7e57c2' }} />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              mb: 2, 
                              fontWeight: 700,
                              fontSize: '18px',
                              color: '#2c3e50',
                              lineHeight: 1.3
                            }}
                          >
                            {doc.documentName}
                          </Typography>
                          {doc.description && (
                            <Box sx={{
                              background: '#ffffff',
                              border: '1px solid #e8eaed',
                              borderRadius: 2,
                              p: 2.5,
                              position: 'relative',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '4px',
                                background: '#7e57c2',
                                borderRadius: '0 2px 2px 0'
                              }
                            }}>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  lineHeight: 1.7,
                                  fontSize: '16px',
                                  fontWeight: 400,
                                  color: '#5f6368',
                                  m: 0
                                }}
                              >
                                {doc.description}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      {doc.docType === 'video/mp4' ? (
                        <>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#5f6368',
                              mb: 2.5
                            }}
                          >
                            Watch this video to understand the content
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button 
                              variant={data[doc.documentId] ? "contained" : "outlined"} 
                              size="medium" 
                              onClick={() => handleOpenVideo(doc)}
                              color="primary"
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                textTransform: 'none',
                                backgroundColor: data[doc.documentId] ? '#7e57c2' : 'transparent',
                                color: data[doc.documentId] ? '#ffffff' : '#7e57c2',
                                borderColor: '#7e57c2',
                                '&:hover': {
                                  backgroundColor: data[doc.documentId] ? '#6a4c93' : 'rgba(126, 87, 194, 0.08)',
                                  borderColor: '#6a4c93'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {data[doc.documentId] ? 'Video Watched' : 'Watch Video'}
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#5f6368',
                              mb: 2.5
                            }}
                          >
                            {subModule === 'Employee Guidelines & Policies' 
                              ? 'Please read and understand these documents, and upload the signed copy in the Process Document Tab'
                              : 'Please read and understand this document'
                            }
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button 
                              variant={data[doc.documentId] ? "contained" : "outlined"} 
                              size="medium" 
                              onClick={() => handleOpenDocument(doc)}
                              color="primary"
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                textTransform: 'none',
                                backgroundColor: data[doc.documentId] ? '#7e57c2' : 'transparent',
                                color: data[doc.documentId] ? '#ffffff' : '#7e57c2',
                                borderColor: '#7e57c2',
                                '&:hover': {
                                  backgroundColor: data[doc.documentId] ? '#6a4c93' : 'rgba(126, 87, 194, 0.08)',
                                  borderColor: '#6a4c93'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {data[doc.documentId] ? 'Document Read' : 'View Document'}
                            </Button>
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}
      </Grid>

      {/* Video Dialog */}
      <Dialog open={videoOpen} onClose={handleCloseVideo} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          {currentDocument?.documentName}
          <IconButton aria-label="close" onClick={handleCloseVideo} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
            <ReactPlayer
              url={`${API_URL}/documents/stream/${currentDocument?.filePath}`}
              width="100%"
              height="100%"
              style={{ position: 'absolute', top: 0, left: 0 }}
              controls
              onProgress={handleVideoProgress}
              onEnded={handleVideoEnd}
            />
          </Box>
        </DialogContent>
      </Dialog>

{/* Document Dialog */}
<Dialog open={documentOpen} onClose={handleCloseDocument} maxWidth="md" fullWidth>
  <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
    {currentDocument?.documentName}
    <IconButton
      aria-label="close"
      onClick={handleCloseDocument}
      sx={{ position: 'absolute', right: 8, top: 8 }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  <DialogContent>
    <iframe
      src={`${API_URL}/documents/stream/${currentDocument?.filePath}`}
      width="100%"
      height="600px"
      title={currentDocument?.documentName}
      style={{ border: 'none' }}
    />
  </DialogContent>
</Dialog>

{/* Early Close Warning Dialog */}
<Dialog open={showEarlyCloseWarning}>
  <DialogTitle>Warning</DialogTitle>
  <DialogContent>
    <Typography>You haven't completed this content. Are you sure you want to close?</Typography>
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
      <Button onClick={() => setShowEarlyCloseWarning(false)}>Cancel</Button>
      <Button
        variant="contained"
        onClick={() => {
          setShowEarlyCloseWarning(false);
          setVideoOpen(false);
          setDocumentOpen(false);
        }}
      >
        Close Anyway
      </Button>
    </Box>
  </DialogContent>
</Dialog>
    </Box>
  );
}   

export default InductionStep;